import { useEffect, useState } from 'react'
import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { quoteApi, documentApi } from '@/services/api'
import type { Quote, Document } from '@/types'
import { FileText, Plus, Send, Check, X, ArrowRightLeft, Copy, Ban, Trash2, Paperclip, File, Image } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/utils/currency'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

function formatSize(bytes: number | null) {
  if (!bytes) return '—'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function DocIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image className="w-3.5 h-3.5 text-blue-400 shrink-0" />
  if (mimeType === 'application/pdf') return <FileText className="w-3.5 h-3.5 text-red-400 shrink-0" />
  return <File className="w-3.5 h-3.5 text-gray-400 shrink-0" />
}

const STATUS_TABS = ['all', 'draft', 'sent', 'accepted', 'declined', 'expired', 'invoiced', 'voided'] as const
type StatusTab = typeof STATUS_TABS[number]

const STATUS_VARIANT: Record<string, 'default' | 'info' | 'success' | 'destructive' | 'warning'> = {
  draft: 'default',
  sent: 'info',
  accepted: 'success',
  declined: 'destructive',
  expired: 'warning',
  invoiced: 'info',
  voided: 'default',
}

export default function QuoteList() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [docs, setDocs] = useState<Document[]>([])
  const [activeTab, setActiveTab] = useState<StatusTab>('all')
  const [loading, setLoading] = useState(true)
  const [premiumGated, setPremiumGated] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) return
    Promise.all([quoteApi.list(clientId), documentApi.list(clientId)])
      .then(([quoteRes, docRes]) => {
        setQuotes(quoteRes.data.quotes)
        setDocs(docRes.data.documents)
      })
      .catch((err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 402) setPremiumGated(true)
        else toast.error('Failed to load quotes')
      })
      .finally(() => setLoading(false))
  }, [clientId])

  function docsFor(quoteId: string) {
    return docs.filter((d) => d.linked_quote_id === quoteId)
  }

  const filtered = activeTab === 'all' ? quotes : quotes.filter((q) => q.status === activeTab)

  async function handleSend(q: Quote) {
    try {
      const res = await quoteApi.send(q.id)
      setQuotes((prev) => prev.map((i) => (i.id === res.data.quote.id ? res.data.quote : i)))
      toast.success('Quote marked as sent')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  async function handleAccept(q: Quote) {
    try {
      const res = await quoteApi.accept(q.id)
      setQuotes((prev) => prev.map((i) => (i.id === res.data.quote.id ? res.data.quote : i)))
      toast.success('Quote accepted')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  async function handleDecline(q: Quote) {
    if (!confirm(`Decline quote ${q.quote_number}?`)) return
    try {
      const res = await quoteApi.decline(q.id)
      setQuotes((prev) => prev.map((i) => (i.id === res.data.quote.id ? res.data.quote : i)))
      toast.success('Quote declined')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  async function handleConvertToInvoice(q: Quote) {
    if (!confirm(`Convert quote ${q.quote_number} to an invoice?`)) return
    try {
      const res = await quoteApi.convertToInvoice(q.id)
      // Update quote status locally
      setQuotes((prev) =>
        prev.map((i) =>
          i.id === q.id
            ? { ...i, status: 'invoiced', converted_invoice_id: res.data.invoice.id, converted_invoice_number: res.data.invoice.invoice_number }
            : i
        )
      )
      toast.success(`Invoice ${res.data.invoice.invoice_number} created from quote`)
      navigate(`/clients/${clientId}/invoices`)
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  async function handleCopy(q: Quote) {
    try {
      const res = await quoteApi.copy(q.id)
      setQuotes((prev) => [res.data.quote, ...prev])
      toast.success(`Quote ${res.data.quote.quote_number} created as copy`)
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  async function handleVoid(q: Quote) {
    if (!confirm(`Void quote ${q.quote_number}?`)) return
    try {
      const res = await quoteApi.void(q.id)
      setQuotes((prev) => prev.map((i) => (i.id === res.data.quote.id ? res.data.quote : i)))
      toast.success('Quote voided')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  async function handleDelete(q: Quote) {
    if (!confirm(`Delete draft quote ${q.quote_number}?`)) return
    try {
      await quoteApi.delete(q.id)
      setQuotes((prev) => prev.filter((i) => i.id !== q.id))
      toast.success('Quote deleted')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  if (premiumGated) {
    return (
      <Card className="max-w-sm mx-auto mt-16">
        <CardContent className="p-10 text-center">
          <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-6 h-6 text-amber-500" />
          </div>
          <h2 className="text-base font-semibold text-[#000A1E] mb-2">Premium Feature</h2>
          <p className="text-sm text-[rgba(0,10,30,0.75)] mb-5">
            Quotes require a Premium client. Upgrade in Client Settings → Plan.
          </p>
          <Button onClick={() => navigate(`/clients/${clientId}/settings`)}>Upgrade to Premium</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Quotes</h1>
          <p className="text-sm text-[rgba(0,10,30,0.75)] mt-0.5">Sales quotes and estimates</p>
        </div>
        <Button variant="create" onClick={() => navigate(`/clients/${clientId}/quotes/new`)}>
          <Plus className="w-4 h-4" /> New Quote
        </Button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-0 border-b-2 border-[#CCCED2]">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-3 text-[13px] -mb-[2px] border-b-[3px] capitalize transition-colors',
              activeTab === tab
                ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                : 'text-[#0078C8] font-normal border-b-transparent hover:text-[#000A1E]'
            )}
          >
            {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Quote #</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-sm text-[rgba(0,10,30,0.75)]">
                      No quotes found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((q) => (
                    <React.Fragment key={q.id}>
                      <TableRow>
                        <TableCell>
                          <button
                            onClick={() => navigate(`/clients/${clientId}/quotes/${q.id}/edit`)}
                            className="text-sm text-[#0078C8] font-bold hover:underline"
                          >
                            {q.quote_number}
                          </button>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-[#000A1E]">{q.contact_name}</p>
                          {q.contact_email && <p className="text-xs text-[rgba(0,10,30,0.75)]">{q.contact_email}</p>}
                        </TableCell>
                        <TableCell className="text-sm text-[rgba(0,10,30,0.75)] max-w-[200px] truncate">
                          {q.title || <span className="text-[#CCCED2]">—</span>}
                        </TableCell>
                        <TableCell className="text-sm text-[rgba(0,10,30,0.75)]">{q.issue_date}</TableCell>
                        <TableCell className="text-sm text-[rgba(0,10,30,0.75)]">
                          {q.expiry_date || <span className="text-[#CCCED2]">—</span>}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium text-[#000A1E] tabular-nums">
                          {formatCurrency(q.total, q.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[q.status] ?? 'default'}>
                            {q.status.charAt(0).toUpperCase() + q.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                              title="Attachments"
                            >
                              <Paperclip className={cn(
                                'w-3.5 h-3.5',
                                docsFor(q.id).length > 0 ? 'text-blue-500' : 'text-gray-300',
                              )} />
                            </Button>
                            {q.status === 'draft' && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleSend(q)} title="Send quote">
                                <Send className="w-3.5 h-3.5 text-blue-500" />
                              </Button>
                            )}
                            {(q.status === 'sent' || q.status === 'draft') && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleAccept(q)} title="Accept quote">
                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                              </Button>
                            )}
                            {(q.status === 'sent' || q.status === 'draft') && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleDecline(q)} title="Decline quote">
                                <X className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                            )}
                            {(q.status === 'accepted' || q.status === 'sent' || q.status === 'draft') && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleConvertToInvoice(q)} title="Convert to invoice">
                                <ArrowRightLeft className="w-3.5 h-3.5 text-purple-500" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon-sm" onClick={() => handleCopy(q)} title="Copy quote">
                              <Copy className="w-3.5 h-3.5 text-gray-500" />
                            </Button>
                            {q.status !== 'invoiced' && q.status !== 'voided' && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleVoid(q)} title="Void quote">
                                <Ban className="w-3.5 h-3.5 text-amber-500" />
                              </Button>
                            )}
                            {q.status === 'draft' && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(q)} title="Delete"
                                className="text-red-400 hover:text-red-600 hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedId === q.id && (
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={8} className="px-6 py-3">
                            {q.converted_invoice_number && (
                              <p className="text-xs text-purple-600 mb-2">
                                Converted to invoice{' '}
                                <button
                                  onClick={() => navigate(`/clients/${clientId}/invoices`)}
                                  className="font-medium underline hover:text-purple-800"
                                >
                                  {q.converted_invoice_number}
                                </button>
                              </p>
                            )}
                            {docsFor(q.id).length === 0 ? (
                              <p className="text-xs text-[rgba(0,10,30,0.75)]">No documents attached to this quote.</p>
                            ) : (
                              <div className="flex flex-wrap gap-3">
                                {docsFor(q.id).map((doc) => (
                                  <a
                                    key={doc.id}
                                    href={doc.file_path}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 text-xs text-[rgba(0,10,30,0.75)] hover:text-[#0078C8] bg-white border border-border rounded-md px-2.5 py-1.5 transition-colors"
                                  >
                                    <DocIcon mimeType={doc.mime_type} />
                                    <span className="font-medium truncate max-w-[160px]">{doc.file_name}</span>
                                    {doc.category && (
                                      <Badge variant="secondary" className="capitalize text-[10px] px-1 py-0">
                                        {doc.category}
                                      </Badge>
                                    )}
                                    <span className="text-[rgba(0,10,30,0.75)]">{formatSize(doc.file_size)}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
